import React, { useState, useEffect, Suspense, useMemo } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import {
	Page,
	Card,
	FooterHelp,
	SkeletonBodyText,
	ContextualSaveBar,
	SkeletonDisplayText,
} from '@shopify/polaris';

import {
	useForm,
	useField,
	submitSuccess,
	submitFail,
	useDynamicList,
	reduceFields,
} from '@shopify/react-form';

import { lazyWithPreload } from 'react-lazy-with-preload';

import HomePanel from './Components/HomePanel';
const HtmlSitemapPanel = lazyWithPreload(() =>
	import('./Components/HtmlSitemapPanel')
);
const XmlSitemapPanel = lazyWithPreload(() =>
	import('./Components/XmlSitemapPanel')
);
const SeoToolPanel = lazyWithPreload(() => import('./Components/SeoToolPanel'));
const IntegrationPanel = lazyWithPreload(() =>
	import('./Components/IntegrationPanel')
);
const FeatureRequestPanel = lazyWithPreload(() =>
	import('./Components/FeatureRequestPanel')
);

import { useList, useMountedState } from 'react-use';
import { HubConnectionBuilder } from '@microsoft/signalr';
import useAbortController from './Utils/useAbortController';
import { useI18n } from '@shopify/react-i18n';
import LanguageSwitcher from './Components/Shared/LanguageSwitcher';
import { useChat } from 'react-live-chat-loader';

const Main = ({ shared }) => {
	const { fetchJson, redirectOpt, showToast, setLocale, user } = shared;
	const abortController = useAbortController();
	const isMounted = useMountedState();
	const [i18n] = useI18n();
	const { pathname } = useLocation();

	const [chatState, loadChat] = useChat();

	const [locales, { set: setLocales }] = useList([]);
	const [lastSyncStore, setLastSyncStore] = useState(user.lastSyncStore);
	const [products, setProducts] = useState(user.products);
	const [syncingProducts, setSyncingProducts] = useState(0);

	const [collections, setCollections] = useState(user.collections);
	const [pages, setPages] = useState(user.pages);
	const [articles, setArticles] = useState(user.articles);
	const [isSubscribed, setIsSubscribed] = useState(user.isSubscribed);
	const [plan, setPlan] = useState(user.plan);
	const [lastSyncTranslation, setLastSyncTranslation] = useState(
		user.lastSyncTranslation
	);

	// Preload components onload
	useEffect(() => {
		HtmlSitemapPanel.preload();
		XmlSitemapPanel.preload();
		SeoToolPanel.preload();
		IntegrationPanel.preload();
		FeatureRequestPanel.preload();
	}, []);

	// Preferences
	const pref = user.preferences;
	const { fields, dynamicLists, submit, reset, submitting, dirty } = useForm({
		fields: {
			pageTitle: useField(pref.pageTitle ?? ''),
			pageDescription: useField(pref.pageDescription ?? ''),
			metaTitle: useField(pref.metaTitle ?? ''),
			metaDescription: useField(pref.metaDescription ?? ''),
			pageTemplate: useField(pref.pageTemplate ?? ''),
			containerCssClass: useField(pref.containerCssClass ?? ''),
			sectionCssClass: useField(pref.sectionCssClass ?? ''),
			usePageBuilder: useField(pref.usePageBuilder),
			builderOptions: useField(pref.builderOptions),
			margin: useField(pref.margin),
			isMultilingual: useField(pref.isMultilingual),
			isHorizontal: useField(pref.isHorizontal),
			isHidingBullets: useField(pref.isHidingBullets),
			isHidingUnderline: useField(pref.isHidingUnderline),
			isShowingAll: useField(pref.isShowingAll),
			isExcludingOutOfStockProducts: useField(
				pref.isExcludingOutOfStockProducts
			),
			isIncludingImagesInXmlSitemap: useField(
				pref.isIncludingImagesInXmlSitemap
			),
			isIncludingBlogTagsInXmlSitemap: useField(
				pref.isIncludingBlogTagsInXmlSitemap
			),
			isIncludingProductTagsInXmlSitemap: useField(
				pref.isIncludingProductTagsInXmlSitemap
			),
			isIncludingProductTypesInXmlSitemap: useField(
				pref.isIncludingProductTypesInXmlSitemap
			),
			isIncludingProductVendorsInXmlSitemap: useField(
				pref.isIncludingProductVendorsInXmlSitemap
			),
			isIncludingProductVariantsInXmlSitemap: useField(
				pref.isIncludingProductVariantsInXmlSitemap
			),
			isIncludingAlternativeLinksInXmlSitemap: useField(
				pref.isIncludingAlternativeLinksInXmlSitemap
			),
			collectionId: useField(pref.collectionId),
			wordPress: {
				wpBaseUrl: useField(pref.wordPress.wpBaseUrl ?? ''),
				wpUsername: useField(pref.wordPress.wpUsername ?? ''),
				wpPassword: useField(pref.wordPress.wpPassword ?? ''),
				wpBlogId: useField(pref.wordPress.wpBlogId ?? null),
			},
			translation: {
				homePage: useField(pref.translation.homePage ?? ''),
				viewMore: useField(pref.translation.viewMore ?? ''),
				pageHeading: useField(pref.translation.pageHeading ?? ''),
				collectionHeading: useField(
					pref.translation.collectionHeading ?? ''
				),
				productHeading: useField(pref.translation.productHeading ?? ''),
				productTypeHeading: useField(
					pref.translation.productTypeHeading ?? ''
				),
				productVendorHeading: useField(
					pref.translation.productVendorHeading ?? ''
				),
				productTagHeading: useField(
					pref.translation.productTagHeading ?? ''
				),
				blogHeading: useField(pref.translation.blogHeading ?? ''),
				blogTagHeading: useField(pref.translation.blogTagHeading ?? ''),
				moreHeading: useField(pref.translation.moreHeading ?? ''),
			},
		},
		dynamicLists: {
			customHtmlLinks: useDynamicList(
				pref.customHtmlLinks,
				(item) => item
			),
			customXmlLinks: useDynamicList(pref.customXmlLinks, (item) => item),
			linkAttributes: useDynamicList(pref.linkAttributes, (item) => item),
			hiddenLinks: useDynamicList(pref.hiddenLinks, (item) => item),
			hiddenUrls: useDynamicList(pref.hiddenUrls, (item) => item),
			urlReplacements: useDynamicList(
				pref.urlReplacements,
				(item) => item
			),
			rssFeeds: useDynamicList(pref.rssFeeds, (item) => item),
		},
		onSubmit: async (fieldValues) => {
			const payload = JSON.stringify(fieldValues);

			const success = await fetchJson(
				'/Dashboard/UpdatePreferences?' +
					new URLSearchParams({
						shop: user.shopifyDomain,
					}),
				'POST',
				payload,
				abortController.signal,
				(pref) => {
					showToast({
						content: i18n.translate('Main.SettingsUpdated'),
						action: {
							content: i18n.translate('Common.Action.View'),
							onAction: () => {
								const sitemapUrl = `${user.url}/${user.pathPrefix}/sitemap`;
								redirectOpt(sitemapUrl, true);
							},
						},
					});

					// Update fields by values returned
					reduceFields(fields, (_, field, path) => {
						field.newDefaultValue(
							path.reduce((acc, c) => acc && acc[c], pref)
						);
					});

					for (const list of Object.keys(dynamicLists)) {
						dynamicLists[list].newDefaultValue(pref[list]);
					}
				}
			);

			return success ? submitSuccess() : submitFail();
		},
	});

	// Init chat session
	useEffect(() => {
		if (chatState == 'complete') {
			window.Beacon('session-data', {
				Shop: user.shopifyDomain,
				Sitemap: `${user.url}/${user.pathPrefix}/sitemap`,
				Plan: plan,
			});
		}
	}, [chatState, plan]);

	// Setup SignalR connection
	useEffect(() => {
		const connection = new HubConnectionBuilder()
			.withUrl('/notifyHub')
			.withAutomaticReconnect()
			.build();

		connection.on('storeSynced', (shop, dateTime) => {
			if (user.shopifyDomain == shop) setLastSyncStore(dateTime);
		});

		connection.on('productsSynced', (shop, count) => {
			if (user.shopifyDomain == shop) setProducts(count);
		});

		connection.on('productsSyncing', (shop, status, count) => {
			if (user.shopifyDomain == shop)
				setSyncingProducts(status ? count : 0);
		});

		connection.on('collectionsSynced', (shop, count) => {
			if (user.shopifyDomain == shop) setCollections(count);
		});

		connection.on('pagesSynced', (shop, count) => {
			if (user.shopifyDomain == shop) setPages(count);
		});

		connection.on('articlesSynced', (shop, count) => {
			if (user.shopifyDomain == shop) setArticles(count);
		});

		connection.on('translationSynced', (shop, dateTime) => {
			if (user.shopifyDomain == shop) setLastSyncTranslation(dateTime);
		});

		connection.start().catch((error) => {
			console.warn(error);
		});

		return () => connection.stop();
	}, []);

	// Fetch locales on load
	useEffect(() => {
		fetchJson(
			'/Dashboard/GetLocales',
			'GET',
			undefined,
			abortController.signal,
			(data) => {
				if (!isMounted()) return;
				setLocales(data);
			}
		);

		return () => abortController.abort();
	}, []);

	const skeletonPage = (
		<Card title={<SkeletonDisplayText />}>
			<Card.Section>
				<SkeletonBodyText />
			</Card.Section>
		</Card>
	);

	const [primaryAction, setPrimaryAction] = useState(null);
	const [secondaryActions, setSecondaryActions] = useState([]);

	const extraShared = {
		...shared,
		isSubscribed,
		setIsSubscribed,
		plan,
		setPlan,
		setPrimaryAction,
		setSecondaryActions,
	};

	const homePanel = (
		<HomePanel
			shared={extraShared}
			lastSyncStore={lastSyncStore}
			products={products}
			syncingProducts={syncingProducts}
			collections={collections}
			pages={pages}
			articles={articles}
		/>
	);

	const htmlPanel = (
		<Suspense fallback={skeletonPage}>
			<HtmlSitemapPanel
				locales={locales}
				shared={extraShared}
				lastSyncTranslation={lastSyncTranslation}
				pageTitle={fields.pageTitle}
				pageDescription={fields.pageDescription}
				metaTitle={fields.metaTitle}
				metaDescription={fields.metaDescription}
				pageTemplate={fields.pageTemplate}
				containerCssClass={fields.containerCssClass}
				sectionCssClass={fields.sectionCssClass}
				usePageBuilder={fields.usePageBuilder}
				builderOptions={fields.builderOptions}
				margin={fields.margin}
				isMultilingual={fields.isMultilingual}
				isHorizontal={fields.isHorizontal}
				isExcludingOutOfStockProducts={
					fields.isExcludingOutOfStockProducts
				}
				isHidingBullets={fields.isHidingBullets}
				isHidingUnderline={fields.isHidingUnderline}
				isShowingAll={fields.isShowingAll}
				customHtmlLinks={dynamicLists.customHtmlLinks}
				wordPress={fields.wordPress}
				rssFeeds={dynamicLists.rssFeeds}
				translation={fields.translation}
				collectionId={fields.collectionId}
				hiddenLinks={dynamicLists.hiddenLinks}
			/>
		</Suspense>
	);

	const xmlPanel = (
		<Suspense fallback={skeletonPage}>
			<XmlSitemapPanel
				shared={extraShared}
				customXmlLinks={dynamicLists.customXmlLinks}
				linkAttributes={dynamicLists.linkAttributes}
				locales={locales}
				isIncludingImagesInXmlSitemap={
					fields.isIncludingImagesInXmlSitemap
				}
				isIncludingBlogTagsInXmlSitemap={
					fields.isIncludingBlogTagsInXmlSitemap
				}
				isIncludingProductTagsInXmlSitemap={
					fields.isIncludingProductTagsInXmlSitemap
				}
				isIncludingProductTypesInXmlSitemap={
					fields.isIncludingProductTypesInXmlSitemap
				}
				isIncludingProductVendorsInXmlSitemap={
					fields.isIncludingProductVendorsInXmlSitemap
				}
				isIncludingProductVariantsInXmlSitemap={
					fields.isIncludingProductVariantsInXmlSitemap
				}
				isIncludingAlternativeLinksInXmlSitemap={
					fields.isIncludingAlternativeLinksInXmlSitemap
				}
				hiddenUrls={dynamicLists.hiddenUrls}
				urlReplacements={dynamicLists.urlReplacements}
			/>
		</Suspense>
	);

	const seoPanel = (
		<Suspense fallback={skeletonPage}>
			<SeoToolPanel shared={extraShared} />
		</Suspense>
	);

	const integrationPanel = (
		<Suspense fallback={skeletonPage}>
			<IntegrationPanel shared={extraShared} />
		</Suspense>
	);

	const featuresPanel = (
		<Suspense fallback={skeletonPage}>
			<FeatureRequestPanel shared={extraShared} />
		</Suspense>
	);

	const pageTitle = useMemo(() => {
		if (pathname == '/html') return i18n.translate('Common.HtmlSitemap');
		else if (pathname == '/xml') return i18n.translate('Common.XmlSitemap');
		else if (pathname == '/seo')
			return i18n.translate('Main.SeoTool.plural');
		else if (pathname == '/integration')
			return i18n.translate('Main.Integration.plural');
		else return i18n.translate('Main.Title');
	}, [pathname, i18n.language]);

	return (
		<Page
			title={pageTitle}
			primaryAction={primaryAction}
			secondaryActions={secondaryActions}
		>
			{dirty && (
				<ContextualSaveBar
					alignContentFlush
					fullWidth
					message={i18n.translate('Main.UnsavedChanges')}
					saveAction={{
						onAction: submit,
						loading: submitting,
					}}
					discardAction={{
						onAction: reset,
					}}
				/>
			)}
			<Routes>
				<Route path='/' element={homePanel} />
				<Route path='/html' element={htmlPanel} />
				<Route path='/xml' element={xmlPanel} />
				<Route path='/seo' element={seoPanel} />
				<Route path='/integration' element={integrationPanel} />
				<Route path='/features' element={featuresPanel} />
			</Routes>
			<FooterHelp>
				<LanguageSwitcher setLocale={setLocale} />
			</FooterHelp>
		</Page>
	);
};

export default Main;
